import React, { useState, useMemo, useCallback, useDeferredValue } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Popper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import DropzoneArea from '../DropzoneArea';
import Constants from '../../../../assests/Constants/constants';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box !important',
});

// This modal's overlay sits at z-index 2000/2001, above the 1300 that MUI's own Autocomplete popper
// slot uses, so the menu would render behind the modal body and look like it never opened. The
// doubled selector is what makes this z-index beat that slot regardless of stylesheet order.
const StyledPopper = styled(Popper)(({ theme }) => ({
    '&&': {
        zIndex: 2100,
    },
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '280px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(249, 170, 42, 0.15) !important',
        },
        '&:hover': {
            background: 'rgba(249, 170, 42, 0.25) !important',
        },
    },
}));

/** How the two legs of an entry were paired out of the single-sided B100 movements. */
const formatPairingMode = (pairingMode, t) => {
    // A refund is reported as the way its legs were found plus the rewrite, e.g. "Transaction+Refund".
    const [mode, ...rest] = String(pairingMode ?? '').trim().split('+');

    const label = (() => {
        switch (mode) {
            case 'Transaction':
                return t('BmkvdPairedByTransaction');
            case 'Batch':
                return t('BmkvdPairedByBatch');
            case 'CounterAccount':
                return t('BmkvdPairedByCounterAccount');
            case 'OneSided':
                return t('BmkvdOneSided');
            case 'GeneralParty':
                return t('BmkvdGeneralParty');
            default:
                return mode || '';
        }
    })();

    return rest.includes('Refund') ? `${label} + ${t('BmkvdRefund')}` : label;
};

/** The uniform file has no sortcodes, so the VAT deduction % is inferred — snap it to a real option. */
const snapVatToOption = (vat) => {
    const v = Number(vat);
    if (Number.isNaN(v)) return null;
    const options = Constants.VATPercentage ?? [];
    if (!options.length) return v;
    return options.reduce(
        (best, opt) => (Math.abs(opt - v) < Math.abs(best - v) ? opt : best),
        options[0]
    );
};

const normalizeBmkvdMissingSortCode = (sc) => {
    const sortCodeName = typeof sc === 'string' ? sc : (sc?.sortCodeName ?? sc?.SortCodeName ?? '');
    const idFromApi = typeof sc === 'string' ? '' : String(sc?.sortCodeId ?? sc?.SortCodeId ?? '').trim();

    const vatType = sc?.sortCodeVatType ?? sc?.SortCodeVatType;
    const vat = typeof sc?.vat === 'number' ? sc.vat : typeof sc?.Vat === 'number' ? sc.Vat : null;
    const par = sc?.participatesInProfitAndLoss ?? sc?.ParticipatesInProfitAndLoss;

    return {
        sortCodeId: /^\d+$/.test(idFromApi) ? idFromApi : (sortCodeName || idFromApi),
        sortCodeName: sortCodeName || idFromApi,
        sortCodeVatType:
            vatType !== null && vatType !== undefined && vatType !== ''
                ? String(vatType).trim()
                : null,
        sortCodeCategory: sc?.sortCodeCategory ?? sc?.SortCodeCategory ?? null,
        participatesInProfitAndLoss: typeof par === 'boolean' ? par : true,
        vat: vat === null ? null : snapVatToOption(vat),
        movementCount: sc?.movementCount ?? sc?.MovementCount ?? 0,
        sampleAccounts: sc?.sampleAccounts ?? sc?.SampleAccounts ?? '',
    };
};

const BmkvdJournalImportModal = ({
    open,
    onClose,
    previewBmkvdJournal,
    commitBmkvdJournal,
    createMissingSortCodes,
    createMissingPalCode,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    palCodeCategoryOptions = [],
    palCodes = [],
    sortCodeIdOptions = [],
    user = {},
}) => {
    const existingSortCodeOptions = useMemo(
        () => (sortCodeIdOptions || []).map(sc => ({
            value: String(sc.sortCodeId),
            label: `${sc.sortCodeId} - ${sc.sortCodeName ?? ''}`,
        })),
        [sortCodeIdOptions]
    );

    const participationOptions = useMemo(
        () => [{ label: t('Yes'), value: true }, { label: t('No'), value: false }],
        [t]
    );

    const vatPercentageOptions = useMemo(
        () => Constants.VATPercentage.map((v) => ({ label: `${v}%`, value: v })),
        []
    );

    const renderPicker = ({
        options,
        value,
        onChange,
        placeholder,
        label,
        multiple = false,
        error = false,
        width,
    }) => (
        <Autocomplete
            multiple={multiple}
            size="small"
            options={options || []}
            getOptionLabel={(option) => option?.label ?? ''}
            value={value}
            onChange={(ev, next) => onChange(multiple ? (next ?? []) : (next ?? null))}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    size="small"
                    error={error}
                />
            )}
            PopperComponent={StyledPopper}
            sx={width ? { minWidth: width } : undefined}
        />
    );

    const renderFilterSelect = ({ label, options, value, onChange, isMulti = true, width = 200 }) => (
        <Box sx={{ minWidth: width, maxWidth: width * 1.6 }}>
            {renderPicker({
                options,
                value,
                label,
                multiple: isMulti,
                onChange: (next) => {
                    onChange(next);
                    setEntriesPage(0);
                },
            })}
        </Box>
    );

    const [journalFile, setJournalFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    // A uniform file carries the client's whole year, so the range scopes what is read rather than
    // just what is shown: the totals, the missing sort codes and the account cards to create all
    // come back for this period alone, and the commit runs with the same range.
    const [documentDateFrom, setDocumentDateFrom] = useState('');
    const [documentDateTo, setDocumentDateTo] = useState('');

    // The range the preview on screen was produced with. Changing a date afterwards leaves the two
    // out of step, and importing then would post a different period than the one reviewed.
    const [previewedRange, setPreviewedRange] = useState(null);

    // Whether to leave out entries the case already holds. It is only ever measured against what is
    // already saved, so a file that lists the same document twice imports it twice either way; this
    // is for the second run over a file whose entries are partly in already. Turn it off to post the
    // file as it stands and let the case hold both copies.
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [previewedSkipDuplicates, setPreviewedSkipDuplicates] = useState(true);

    const [entries, setEntries] = useState([]);
    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [missingPalCodes, setMissingPalCodes] = useState([]);

    // Creating the main accounts the file's trial-balance groups need, mirroring the Excel
    // account-cards import. A group created here is matched by name on commit, so the cards that
    // reference it stop being created without a main account.
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [palCodeBulkMode, setPalCodeBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [createdPalCodes, setCreatedPalCodes] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({
        palCodeId: '',
        palCodeType: '',
        palCodeCategory: null,
    });
    const [palCodeBulkForm, setPalCodeBulkForm] = useState({
        palCodeType: '',
        palCodeCategory: null,
    });

    const palCodeTypeOptions = useMemo(
        () => (Constants.PalCodeType || []).map((type) => ({ label: type, value: type })),
        [],
    );

    const palCodeCategorySelectOptions = useMemo(
        () => (palCodeCategoryOptions || []).map((cat) => ({
            label: cat.palCodeCategoryId,
            value: cat.palCodeCategoryId,
        })),
        [palCodeCategoryOptions],
    );

    const calculateNextPalCodeOrderNumber = useCallback((category) => {
        const all = [...(palCodes || []), ...createdPalCodes];
        if (!category || all.length === 0) return 100;

        const maxOrderNumber = all
            .filter((pc) => pc.palCodeCategory === category)
            .reduce((max, pc) => Math.max(max, pc.orderNumber || 0), 0);

        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    }, [palCodes, createdPalCodes]);

    /// Once a main account exists, the cards that referenced that trial-balance group will pick it
    /// up on commit — reflect that in the table straight away so the preview stays truthful.
    const applyCreatedPalCode = (palCodeId) => {
        setMissingPalCodes((prev) => prev.filter((name) => name !== palCodeId));
        setMissingAccountCards((prev) => prev.map((card) => (
            card.trialBalanceName === palCodeId ? { ...card, palCode: palCodeId } : card
        )));
    };

    const buildPalCodePayload = (palCodeId, form) => ({
        palCodeId,
        palCodeType: form.palCodeType,
        palCodeCategory: form.palCodeCategory,
        orderNumber: form.palCodeCategory ? calculateNextPalCodeOrderNumber(form.palCodeCategory) : 100,
        accountantId: user?.accountantId,
    });

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) return;

        try {
            setProcessing(true);
            const payload = buildPalCodePayload(palCodeForm.palCodeId, palCodeForm);
            const response = await createMissingPalCode(payload);
            if (!response?.ok) throw new Error(await response?.text?.());

            setCreatedPalCodes((prev) => [...prev, payload]);
            applyCreatedPalCode(palCodeName);
            setEditingPalCode(null);
            setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('BMKVD PalCode creation failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!palCodeBulkForm.palCodeType || selectedPalCodesForBulk.length === 0) return;

        try {
            setProcessing(true);
            const base = palCodeBulkForm.palCodeCategory
                ? calculateNextPalCodeOrderNumber(palCodeBulkForm.palCodeCategory)
                : 100;

            const payloads = selectedPalCodesForBulk.map((name, index) => ({
                ...buildPalCodePayload(name, palCodeBulkForm),
                orderNumber: base + index * 100,
            }));

            const responses = await Promise.all(payloads.map((p) => createMissingPalCode(p)));
            const failed = responses.filter((r) => !r?.ok);
            if (failed.length) throw new Error(`${failed.length} PalCodes failed to create`);

            setCreatedPalCodes((prev) => [...prev, ...payloads]);
            selectedPalCodesForBulk.forEach(applyCreatedPalCode);
            setSelectedPalCodesForBulk([]);
            setPalCodeBulkForm({ palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('BMKVD bulk PalCode creation failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const resetPalCodeCreation = () => {
        setEditingPalCode(null);
        setPalCodeBulkMode(false);
        setSelectedPalCodesForBulk([]);
        setCreatedPalCodes([]);
        setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        setPalCodeBulkForm({ palCodeType: '', palCodeCategory: null });
    };
    const [editableMissing, setEditableMissing] = useState([]);
    const [fileStats, setFileStats] = useState(null);
    const [previewError, setPreviewError] = useState(null);

    // The reconciliations (התאמות) the file records in field 1371, and whether to recreate them as
    // card matches. On by default when the file has any, because a file that carries them was
    // reconciled by hand once already and there is no reason to make someone do it again.
    const [reconciliations, setReconciliations] = useState(null);
    const [importReconciliations, setImportReconciliations] = useState(true);
    const [reconciliationSearch, setReconciliationSearch] = useState('');
    const [reconciliationsPage, setReconciliationsPage] = useState(0);
    const [reconciliationsRowsPerPage, setReconciliationsRowsPerPage] = useState(10);
    const [showSkippedReconciliations, setShowSkippedReconciliations] = useState(false);

    // Transaction type -> existing sort code id. A type here is not created; its entries are posted
    // to the sort code the user picked, which is the way to correct an inferred type.
    const [sortCodeAssignments, setSortCodeAssignments] = useState({});

    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);
    const [bulkFieldsChanged, setBulkFieldsChanged] = useState({
        vatType: false,
        group: false,
        par: false,
        vat: false
    });

    const [entriesPage, setEntriesPage] = useState(0);
    const [entriesRowsPerPage, setEntriesRowsPerPage] = useState(25);
    const [entriesOrderBy, setEntriesOrderBy] = useState('lineNumber');
    const [entriesOrder, setEntriesOrder] = useState('asc');
    const [entriesSearch, setEntriesSearch] = useState('');

    // A uniform file yields thousands of entries. Filtering against the typed value on every
    // keystroke blocks the input, so the field updates immediately and the table catches up.
    const deferredSearch = useDeferredValue(entriesSearch);

    const [filterTransactionTypes, setFilterTransactionTypes] = useState([]);
    const [filterSortCodeIds, setFilterSortCodeIds] = useState([]);
    const [filterDebitAccounts, setFilterDebitAccounts] = useState([]);
    const [filterCreditAccounts, setFilterCreditAccounts] = useState([]);
    const [filterPairingModes, setFilterPairingModes] = useState([]);
    const [filterStatus, setFilterStatus] = useState(null);

    const resetFilters = () => {
        setFilterTransactionTypes([]);
        setFilterSortCodeIds([]);
        setFilterDebitAccounts([]);
        setFilterCreditAccounts([]);
        setFilterPairingModes([]);
        setFilterStatus(null);
    };

    const isUnsupportedBmkvdFile = (file) => {
        if (!file?.name) return false;
        const name = file.name.toLowerCase();
        return name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls')
            || name.endsWith('.pdf') || name.endsWith('.mdb');
    };

    const handleJournalFileDrop = files => {
        if (!files?.length) return;
        const file = files[0];
        setJournalFile(file);
        setPreviewDone(false);
        setPreviewError(null);
        setPreviewedRange(null);
        setEntriesPage(0);
        setEntriesSearch('');
        if (isUnsupportedBmkvdFile(file)) {
            setPreviewError(t('BmkvdWrongFileType'));
        }
    };

    const dateRange = useMemo(() => ({
        documentDateFrom: documentDateFrom || null,
        documentDateTo: documentDateTo || null,
    }), [documentDateFrom, documentDateTo]);

    const isRangeInvalid = Boolean(documentDateFrom && documentDateTo && documentDateFrom > documentDateTo);

    const isPreviewStale = Boolean(
        previewDone && previewedRange &&
        (previewedRange.documentDateFrom !== dateRange.documentDateFrom ||
            previewedRange.documentDateTo !== dateRange.documentDateTo ||
            previewedSkipDuplicates !== skipDuplicates)
    );

    const handlePreview = async () => {
        if (!journalFile) return;

        if (isUnsupportedBmkvdFile(journalFile)) {
            setPreviewError(t('BmkvdWrongFileType'));
            return;
        }

        if (isRangeInvalid) {
            setPreviewError(t('BmkvdDateRangeInvalid'));
            return;
        }

        try {
            setProcessing(true);
            setPreviewDone(false);
            setPreviewError(null);

            const res = await previewBmkvdJournal(journalFile, dateRange, skipDuplicates);

            const journalEntries = res?.entries ?? [];
            const missingSortCodes = res?.missingSortCodes ?? [];
            const missingCards = res?.missingAccountCards ?? [];

            if (!journalEntries.length) {
                const hasRange = Boolean(dateRange.documentDateFrom || dateRange.documentDateTo);
                setPreviewError(
                    hasRange
                        ? t('BmkvdDateRangeEmpty')
                        : (res?.errorMessages?.[0] || t('BmkvdPreviewEmpty'))
                );
                setPreviewDone(false);
                return;
            }

            setEntries(journalEntries);
            setMissingAccountCards(missingCards);
            setMissingPalCodes(res?.missingPalCodes ?? []);
            setFileStats({
                vatId: res?.vatId ?? '',
                movementCount: res?.movementCount ?? 0,
                accountCount: res?.accountCount ?? 0,
                zeroAmountMovementCount: res?.zeroAmountMovementCount ?? 0,
                reversedMovementCount: res?.reversedMovementCount ?? 0,
                transactionCount: res?.transactionCount ?? 0,
                batchCount: res?.batchCount ?? 0,
                balancedByTransactionCount: res?.balancedByTransactionCount ?? 0,
                balancedByBatchCount: res?.balancedByBatchCount ?? 0,
                unbalancedGroupCount: res?.unbalancedGroupCount ?? 0,
                totalDebit: res?.totalDebit ?? 0,
                totalCredit: res?.totalCredit ?? 0,
                isBalanced: !!res?.isBalanced,
                dateFrom: res?.dateFrom ?? '',
                dateTo: res?.dateTo ?? '',
                parsedRows: res?.parsedRows ?? journalEntries.length,
                validCount: res?.validCount ?? 0,
                errorCount: res?.errorCount ?? 0,
                duplicateCount: res?.duplicateCount ?? 0,
                conflictingCardIdCount: res?.conflictingCardIdCount ?? 0,
                fallbackSortCodeCount: res?.fallbackSortCodeCount ?? 0,
                generalVatSortCodeCount: res?.generalVatSortCodeCount ?? 0,
                detectedVatAccounts: res?.detectedVatAccounts ?? [],
                collapsedVatLegCount: res?.collapsedVatLegCount ?? 0,
                oneSidedEntryCount: res?.oneSidedEntryCount ?? 0,
                partylessDocumentCount: res?.partylessDocumentCount ?? 0,
                vatAccountRoles: res?.vatAccountRoles ?? [],
                refundCount: res?.refundCount ?? 0,
            });

            setReconciliations(res?.reconciliations ?? null);
            setImportReconciliations(Boolean(res?.reconciliations?.importableCount));
            setReconciliationSearch('');
            setReconciliationsPage(0);
            setShowSkippedReconciliations(false);

            setEditableMissing(missingSortCodes.map(normalizeBmkvdMissingSortCode));
            setSortCodeAssignments({});

            setEntriesPage(0);
            setEntriesSearch('');
            resetFilters();
            resetPalCodeCreation();
            setEntriesOrderBy('lineNumber');
            setEntriesOrder('asc');
            setPreviewedRange(dateRange);
            setPreviewedSkipDuplicates(skipDuplicates);
            setPreviewDone(true);
        } catch (err) {
            console.error('BMKVD journal preview failed:', err);
            setPreviewDone(false);
            setPreviewError(err?.message || t('BmkvdPreviewFailed'));
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!journalFile || isPreviewStale) return;

        try {
            setProcessing(true);

            if (sortCodesToCreate.length > 0) {
                await createMissingSortCodes(sortCodesToCreate);
            }

            // Only ask for the reconciliations when the preview found some to create, so a file
            // without them does not come back reporting that none were imported.
            const withReconciliations = Boolean(importReconciliations && reconciliations?.importableCount);

            const res = await commitBmkvdJournal(
                journalFile, sortCodeAssignments, previewedRange ?? dateRange, withReconciliations,
                skipDuplicates);

            const errorCount = res?.errorCount ?? res?.item2 ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.item3 ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('BMKVD journal commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyBulk = () => {
        setEditableMissing(prev =>
            prev.map(r =>
                selectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: bulkFieldsChanged.vatType ? (bulkVatType?.value ?? null) : r.sortCodeVatType,
                        sortCodeCategory: bulkFieldsChanged.group ? (bulkGroup?.value ?? null) : r.sortCodeCategory,
                        participatesInProfitAndLoss: bulkFieldsChanged.par ? (bulkPar?.value ?? null) : r.participatesInProfitAndLoss,
                        vat: bulkFieldsChanged.vat ? (bulkVat?.value ?? null) : r.vat,
                    }
                    : r
            )
        );
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
        setBulkFieldsChanged({ vatType: false, group: false, par: false, vat: false });
    };

    // A type assigned to an existing sort code is not going to be created, so its own fields are
    // irrelevant and must not hold the commit back.
    const sortCodesToCreate = useMemo(
        () => editableMissing.filter(sc => !sortCodeAssignments[sc.sortCodeName]),
        [editableMissing, sortCodeAssignments]
    );

    const missingIncomplete = useMemo(
        () => sortCodesToCreate.some(sc =>
            !sc.sortCodeName ||
            !sc.sortCodeName.trim() ||
            sc.sortCodeVatType === null ||
            sc.sortCodeVatType === undefined ||
            sc.participatesInProfitAndLoss === null ||
            sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null ||
            sc.vat === undefined
        ),
        [sortCodesToCreate]
    );

    const previewSummaries = useMemo(() => {
        const totalEntries = entries.length;
        const oneSided = entries.filter((e) => e.pairingMode === 'OneSided').length;
        const duplicates = entries.filter((e) => e.isDuplicate).length;
        const withProblems = entries.filter((e) => !e.isValid && !e.isDuplicate).length;
        const sumAmount = entries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

        const uniqueAccounts = new Set(
            entries.flatMap((e) => [e.debitAccount, e.creditAccount]).filter(Boolean)
        ).size;

        const typeMap = new Map();
        entries.forEach((e) => {
            const key = String(e.transactionType ?? '').trim() || t('BmkvdNoTransactionType');
            if (!typeMap.has(key)) {
                typeMap.set(key, { transactionType: key, count: 0, amount: 0, sortCodeId: e.sortCodeId ?? '' });
            }
            const row = typeMap.get(key);
            row.count += 1;
            row.amount += Number(e.amount) || 0;
            if (!row.sortCodeId && e.sortCodeId) row.sortCodeId = e.sortCodeId;
        });

        return {
            totalEntries,
            oneSided,
            duplicates,
            withProblems,
            sumAmount,
            uniqueAccounts,
            typeBreakdown: [...typeMap.values()].sort((a, b) => b.count - a.count),
            missingSortCodes: editableMissing.length,
            cardsToAutoCreate: missingAccountCards.length,
        };
    }, [entries, editableMissing.length, missingAccountCards.length, t]);

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yy = String(d.getFullYear()).slice(-2);
            return `${dd}/${mm}/${yy}`;
        } catch {
            return '';
        }
    };

    const formatAmount = (n) => {
        if (n === null || n === undefined) return '';
        const num = typeof n === 'number' ? n : parseFloat(n);
        if (isNaN(num)) return '';
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Built once per preview rather than per keystroke — this is what keeps the search usable.
    const indexedEntries = useMemo(() => entries.map((e) => ({
        entry: e,
        search: [
            e.lineNumber,
            formatDate(e.documentDate),
            e.reference,
            e.reference2,
            e.transactionNumber,
            e.batchNumber,
            e.debitAccount,
            e.debitAccountName,
            e.creditAccount,
            e.creditAccountName,
            e.amount,
            e.description,
            e.transactionType,
            e.sortCodeId,
            e.sortCodeName,
            formatPairingMode(e.pairingMode, t),
        ]
            .filter((v) => v !== null && v !== undefined && v !== '')
            .join(' ')
            .toLowerCase(),
    })), [entries, t]);

    const transactionTypeOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => set.add(String(e.transactionType ?? '').trim()));
        return [...set]
            .map((value) => ({ value, label: value || t('BmkvdNoTransactionType') }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    }, [entries, t]);

    const sortCodeFilterOptions = useMemo(() => {
        const map = new Map();
        entries.forEach((e) => {
            const id = e.sortCodeId || '';
            const name = e.sortCodeName || '';
            const key = id || name;
            if (!key || map.has(key)) return;
            map.set(key, { value: key, label: id && name ? `${id} - ${name}` : (id || name) });
        });
        return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    }, [entries]);

    const debitAccountOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => { if (e.debitAccount) set.add(e.debitAccount); });
        return [...set]
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map((value) => ({ value, label: value }));
    }, [entries]);

    const creditAccountOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => { if (e.creditAccount) set.add(e.creditAccount); });
        return [...set]
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .map((value) => ({ value, label: value }));
    }, [entries]);

    const pairingModeOptions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => { if (e.pairingMode) set.add(e.pairingMode); });
        return [...set].map((value) => ({ value, label: formatPairingMode(value, t) }));
    }, [entries, t]);

    const statusFilterOptions = useMemo(() => [
        { value: 'valid', label: t('Valid') || 'Valid' },
        { value: 'duplicate', label: t('Duplicate') || 'Duplicate' },
        { value: 'error', label: t('Errors') || 'Error' },
    ], [t]);

    const columnFilteredEntries = useMemo(() => {
        const noFilters = !filterTransactionTypes.length && !filterSortCodeIds.length
            && !filterDebitAccounts.length && !filterCreditAccounts.length
            && !filterPairingModes.length && !filterStatus;
        if (noFilters) return indexedEntries;

        const types = new Set(filterTransactionTypes.map((o) => o.value));
        const sortCodes = new Set(filterSortCodeIds.map((o) => o.value));
        const debits = new Set(filterDebitAccounts.map((o) => o.value));
        const credits = new Set(filterCreditAccounts.map((o) => o.value));
        const pairings = new Set(filterPairingModes.map((o) => o.value));

        return indexedEntries.filter(({ entry }) => {
            if (types.size && !types.has(String(entry.transactionType ?? '').trim())) return false;
            if (sortCodes.size && !sortCodes.has(entry.sortCodeId || entry.sortCodeName || '')) return false;
            if (debits.size && !debits.has(entry.debitAccount)) return false;
            if (credits.size && !credits.has(entry.creditAccount)) return false;
            if (pairings.size && !pairings.has(entry.pairingMode)) return false;
            if (filterStatus?.value === 'valid' && !(entry.isValid && !entry.isDuplicate)) return false;
            if (filterStatus?.value === 'duplicate' && !entry.isDuplicate) return false;
            if (filterStatus?.value === 'error' && !(!entry.isValid && !entry.isDuplicate)) return false;
            return true;
        });
    }, [indexedEntries, filterTransactionTypes, filterSortCodeIds, filterDebitAccounts,
        filterCreditAccounts, filterPairingModes, filterStatus]);

    const filteredEntries = useMemo(() => {
        const q = deferredSearch.trim().toLowerCase();
        const base = q ? columnFilteredEntries.filter((r) => r.search.includes(q)) : columnFilteredEntries;
        return base.map((r) => r.entry);
    }, [columnFilteredEntries, deferredSearch]);

    const hasActiveFilters = filterTransactionTypes.length > 0 || filterSortCodeIds.length > 0
        || filterDebitAccounts.length > 0 || filterCreditAccounts.length > 0
        || filterPairingModes.length > 0 || !!filterStatus || entriesSearch.trim().length > 0;

    const getEntryComparator = useCallback((order, orderBy) => (a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
        if (order === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    }, []);

    const sortedEntries = useMemo(() => {
        if (!filteredEntries.length) return [];
        return [...filteredEntries].sort(getEntryComparator(entriesOrder, entriesOrderBy));
    }, [filteredEntries, entriesOrder, entriesOrderBy, getEntryComparator]);

    const paginatedEntries = useMemo(
        () => sortedEntries.slice(
            entriesPage * entriesRowsPerPage,
            entriesPage * entriesRowsPerPage + entriesRowsPerPage
        ),
        [sortedEntries, entriesPage, entriesRowsPerPage]
    );

    const handleEntriesSort = (property) => {
        const isAsc = entriesOrderBy === property && entriesOrder === 'asc';
        setEntriesOrder(isAsc ? 'desc' : 'asc');
        setEntriesOrderBy(property);
        setEntriesPage(0);
    };

    /**
     * The reconciliations the panel lists, narrowed by the search box. A file brings over a thousand,
     * so the search is how a particular supplier or a particular file number gets found.
     */
    const filteredReconciliations = useMemo(() => {
        const items = reconciliations?.items ?? [];
        const needle = reconciliationSearch.trim().toLowerCase();
        if (!needle) return items;

        return items.filter((item) => [
            item.fileNumber,
            item.accountCode,
            item.accountName,
            item.cardId,
            item.transactionDate,
            ...(item.legs ?? []).map((leg) => String(leg.lineNumber)),
        ].some((field) => String(field ?? '').toLowerCase().includes(needle)));
    }, [reconciliations, reconciliationSearch]);

    const pagedReconciliations = useMemo(() => {
        const start = reconciliationsPage * reconciliationsRowsPerPage;
        return filteredReconciliations.slice(start, start + reconciliationsRowsPerPage);
    }, [filteredReconciliations, reconciliationsPage, reconciliationsRowsPerPage]);

    const renderEntriesSortHeader = (id, label, align = 'left') => (
        <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
            <TableSortLabel
                active={entriesOrderBy === id}
                direction={entriesOrderBy === id ? entriesOrder : 'asc'}
                onClick={() => handleEntriesSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const handleClose = () => {
        setJournalFile(null);
        setPreviewDone(false);
        setEntries([]);
        setMissingAccountCards([]);
        setMissingPalCodes([]);
        setEditableMissing([]);
        setSortCodeAssignments({});
        setFileStats(null);
        setSelectedRows([]);
        setPreviewError(null);
        setEntriesPage(0);
        setEntriesSearch('');
        setDocumentDateFrom('');
        setDocumentDateTo('');
        setPreviewedRange(null);
        setSkipDuplicates(true);
        setPreviewedSkipDuplicates(true);
        setReconciliations(null);
        setImportReconciliations(true);
        setReconciliationSearch('');
        setReconciliationsPage(0);
        setShowSkippedReconciliations(false);
        resetFilters();
        resetPalCodeCreation();
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportBmkvdJournalEntries')}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                        {t('BmkvdFileFormat')}
                    </p>
                    <p style={{ fontSize: '12px', color: '#888', marginBottom: '15px', textAlign: direction === 'rtl' ? 'right' : 'left', lineHeight: 1.5 }}>
                        {t('BmkvdFileFormatHelp')}
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h5>{t('JournalEntriesFile')}</h5>
                            <DropzoneArea
                                key={journalFile ? journalFile.name : 'bmkvd-journal-empty'}
                                onDrop={handleJournalFileDrop}
                                filesLimit={1}
                            />
                            {journalFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {journalFile.name}</span>
                                </div>
                            )}
                        </div>

                        <div>
                            <h5>{t('BmkvdDateRange')}</h5>
                            <p style={{ fontSize: 12, color: '#666', margin: '0 0 10px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                {t('BmkvdDateRangeHelp')}
                            </p>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                <TextField
                                    type="date"
                                    size="small"
                                    label={t('DocumentDateFrom')}
                                    value={documentDateFrom}
                                    onChange={e => setDocumentDateFrom(e.target.value)}
                                    error={isRangeInvalid}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 180 }}
                                />
                                <TextField
                                    type="date"
                                    size="small"
                                    label={t('DocumentDateTo')}
                                    value={documentDateTo}
                                    onChange={e => setDocumentDateTo(e.target.value)}
                                    error={isRangeInvalid}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 180 }}
                                />
                                {(documentDateFrom || documentDateTo) && (
                                    <Button
                                        variant="text"
                                        onClick={() => { setDocumentDateFrom(''); setDocumentDateTo(''); }}
                                    >
                                        {t('ClearFilters')}
                                    </Button>
                                )}
                            </div>
                            {isRangeInvalid && (
                                <p style={{ fontSize: 12, color: '#b71c1c', margin: '8px 0 0' }}>
                                    {t('BmkvdDateRangeInvalid')}
                                </p>
                            )}
                        </div>

                        <div>
                            <h5>{t('BmkvdDuplicates')}</h5>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={skipDuplicates}
                                        onChange={e => setSkipDuplicates(e.target.checked)}
                                    />
                                }
                                label={t('BmkvdSkipDuplicatesLabel')}
                                sx={{ m: 0 }}
                            />
                            <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0', textAlign: direction === 'rtl' ? 'right' : 'left', lineHeight: 1.5 }}>
                                {t('BmkvdSkipDuplicatesHelp')}
                            </p>
                        </div>
                    </div>

                    {isPreviewStale && (
                        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fff8e1', borderRadius: 6, border: '1px solid #ffb300', color: '#8a6d00', fontSize: '14px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                            {previewedSkipDuplicates !== skipDuplicates
                                ? t('BmkvdPreviewOptionsChangedNotice')
                                : t('BmkvdDateRangeChangedNotice')}
                        </div>
                    )}

                    {previewError && (
                        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fdecea', borderRadius: 6, border: '1px solid #f44336', color: '#b71c1c', fontSize: '14px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                            {previewError}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!journalFile || processing || isRangeInvalid}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingIncomplete || isPreviewStale}
                            onClick={handleCreateAndCommit}
                        >
                            {processing ? t('Processing') : t('CreateAndImport')}
                        </Button>
                    </div>

                    {previewDone && fileStats && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div
                                style={{
                                    marginBottom: 15,
                                    padding: 12,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 6,
                                    border: '1px solid #e0e0e0',
                                    textAlign: direction === 'rtl' ? 'right' : 'left',
                                }}
                            >
                                <p style={{ fontSize: '14px', color: '#444', margin: '0 0 8px' }}>
                                    {t('BmkvdMovements')}: <strong>{fileStats.movementCount}</strong>
                                    {' | '}
                                    {t('JournalEntries')}: <strong>{previewSummaries.totalEntries}</strong>
                                    {' | '}
                                    {t('BmkvdAccountsInFile')}: <strong>{fileStats.accountCount}</strong>
                                    {fileStats.vatId ? (
                                        <>
                                            {' | '}
                                            {t('TaxId')}: <strong>{fileStats.vatId}</strong>
                                        </>
                                    ) : null}
                                </p>
                                <p style={{ fontSize: '14px', color: '#444', margin: '0 0 8px' }}>
                                    {t('BmkvdTransactions')}: <strong>{fileStats.transactionCount}</strong>
                                    {' | '}
                                    {t('BmkvdBatches')}: <strong>{fileStats.batchCount}</strong>
                                    {' | '}
                                    {t('DateRange')}: <strong>{formatDate(fileStats.dateFrom)} - {formatDate(fileStats.dateTo)}</strong>
                                </p>
                                <p style={{ fontSize: '14px', color: '#444', margin: '0 0 8px' }}>
                                    {t('BmkvdPairedByTransaction')}: <strong>{fileStats.balancedByTransactionCount}</strong>
                                    {' | '}
                                    {t('BmkvdPairedByBatch')}: <strong>{fileStats.balancedByBatchCount}</strong>
                                    {' | '}
                                    {t('BmkvdOneSided')}: <strong>{previewSummaries.oneSided}</strong>
                                    {fileStats.reversedMovementCount > 0 && (
                                        <>
                                            {' | '}
                                            {t('BmkvdReversedMovements')}: <strong>{fileStats.reversedMovementCount}</strong>
                                        </>
                                    )}
                                </p>
                                <p style={{ fontSize: '14px', color: '#444', margin: '0 0 8px', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                    <span>
                                        {t('TotalDebit')}: <strong>{formatAmount(fileStats.totalDebit)}</strong>
                                        {' | '}
                                        {t('TotalCredit')}: <strong>{formatAmount(fileStats.totalCredit)}</strong>
                                    </span>
                                    <Chip
                                        size="small"
                                        color={fileStats.isBalanced ? 'success' : 'error'}
                                        label={fileStats.isBalanced ? t('BmkvdBalanced') : t('BmkvdNotBalanced')}
                                    />
                                </p>
                                <p style={{ fontSize: '14px', color: '#666', margin: '0 0 4px' }}>
                                    {t('MissingSortCodes')}: <strong>{previewSummaries.missingSortCodes}</strong>
                                    {' | '}
                                    {t('CardsToAutoCreate')}: <strong>{previewSummaries.cardsToAutoCreate}</strong>
                                    {fileStats.conflictingCardIdCount > 0 && (
                                        <>
                                            {' | '}
                                            {t('BmkvdConflictingCardIds')}: <strong>{fileStats.conflictingCardIdCount}</strong>
                                        </>
                                    )}
                                    {' | '}
                                    {t('Duplicates')}: <strong>{previewSummaries.duplicates}</strong>
                                    {previewSummaries.duplicates > 0 && (
                                        <>
                                            {' '}
                                            <span style={{ color: skipDuplicates ? '#8a6d00' : '#1b5e20' }}>
                                                ({skipDuplicates ? t('BmkvdDuplicatesSkipped') : t('BmkvdDuplicatesImported')})
                                            </span>
                                        </>
                                    )}
                                    {previewSummaries.withProblems > 0 && (
                                        <>
                                            {' | '}
                                            {t('ErrorCount')}: <strong>{previewSummaries.withProblems}</strong>
                                        </>
                                    )}
                                </p>
                                {fileStats.generalVatSortCodeCount > 0 && (
                                    <p style={{ fontSize: '13px', color: '#1b5e20', margin: '0 0 4px' }}>
                                        {t('BmkvdGeneralVatSortCodeNotice', { count: fileStats.generalVatSortCodeCount })}
                                    </p>
                                )}
                                {fileStats.fallbackSortCodeCount > 0 && (
                                    <p style={{ fontSize: '13px', color: '#8a6d00', margin: '0 0 4px' }}>
                                        {t('BmkvdFallbackSortCodeNotice', { count: fileStats.fallbackSortCodeCount })}
                                    </p>
                                )}
                                {fileStats.detectedVatAccounts?.length > 0 && (
                                    <p style={{ fontSize: '13px', color: '#1565c0', margin: '0 0 4px' }}>
                                        {t('BmkvdDetectedVatAccounts')}:{' '}
                                        <strong>{fileStats.detectedVatAccounts.join(' | ')}</strong>
                                    </p>
                                )}
                                {fileStats.collapsedVatLegCount > 0 && (
                                    <p style={{ fontSize: '13px', color: '#1565c0', margin: '0 0 4px' }}>
                                        {t('BmkvdCollapsedVatLegsNotice', { count: fileStats.collapsedVatLegCount })}
                                    </p>
                                )}
                                {fileStats.partylessDocumentCount > 0 && (
                                    <p style={{ fontSize: '13px', color: '#1565c0', margin: '0 0 4px' }}>
                                        {t('BmkvdPartylessDocumentsNotice', { count: fileStats.partylessDocumentCount })}
                                    </p>
                                )}
                                {fileStats.oneSidedEntryCount > 0 && (
                                    <p style={{ fontSize: '13px', color: '#8a6d00', margin: '0 0 4px' }}>
                                        {t('BmkvdOneSidedEntriesNotice', { count: fileStats.oneSidedEntryCount })}
                                    </p>
                                )}
                                {fileStats.refundCount > 0 && (
                                    <p style={{ fontSize: '13px', color: '#8a6d00', margin: '0 0 4px' }}>
                                        {t('BmkvdRefundsNotice', { count: fileStats.refundCount })}
                                    </p>
                                )}

                                {fileStats.vatAccountRoles?.length > 0 && (
                                    <>
                                        <h6 style={{ margin: '12px 0 4px', fontSize: 13, fontWeight: 600, color: '#333' }}>
                                            {t('BmkvdVatAccountRoles')}
                                        </h6>
                                        <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px' }}>
                                            {t('BmkvdVatAccountRolesHelp')}
                                        </p>
                                        <table
                                            className="sortcode-table"
                                            style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, fontSize: 13 }}
                                        >
                                            <thead>
                                                <tr style={{ backgroundColor: '#e8e8e8' }}>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('BmkvdVatRole')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('BmkvdVatRoleFileAccounts')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('BmkvdVatRoleSystemCard')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('BmkvdVatRoleGeneralSortCode')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {t('Quantity')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {t('BmkvdVatRoleContraSide')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fileStats.vatAccountRoles.map((role) => (
                                                    <tr key={role.sortCodeVatType}>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {role.roleName}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {role.fileAccounts?.length
                                                                ? role.fileAccounts.join(' | ')
                                                                : <span style={{ color: '#999' }}>{t('BmkvdVatRoleNoFileAccount')}</span>}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {[role.systemCardId, role.systemCardName].filter(Boolean).join(' - ')}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {role.generalSortCodeId
                                                                ? [role.generalSortCodeId, role.generalSortCodeName].filter(Boolean).join(' - ')
                                                                : <span style={{ color: '#b71c1c' }}>{t('BmkvdVatRoleNoGeneralSortCode')}</span>}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                            {role.documentCount}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                            {role.contraSideDocumentCount}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </>
                                )}

                                {previewSummaries.typeBreakdown.length > 0 && (
                                    <>
                                        <h6 style={{ margin: '12px 0 8px', fontSize: 13, fontWeight: 600, color: '#333' }}>
                                            {t('BmkvdSummaryByTransactionType')}
                                        </h6>
                                        <table
                                            className="sortcode-table"
                                            style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, fontSize: 13 }}
                                        >
                                            <thead>
                                                <tr style={{ backgroundColor: '#e8e8e8' }}>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('BmkvdTransactionType')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('SortCode Id')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {t('Quantity')}
                                                    </th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {t('Amount')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewSummaries.typeBreakdown.map((row) => (
                                                    <tr key={row.transactionType}>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {row.transactionType}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {row.sortCodeId || ''}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                            {row.count}
                                                        </td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                            {formatAmount(row.amount)}
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 700 }}>
                                                    <td colSpan={2} style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {t('Total')}
                                                    </td>
                                                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                        {previewSummaries.totalEntries}
                                                    </td>
                                                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                        {formatAmount(previewSummaries.sumAmount)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>

                            {/* Reconciliations (התאמות) the file records in field 1371 */}
                            {reconciliations && (
                                <div
                                    style={{
                                        marginBottom: 15,
                                        padding: 12,
                                        backgroundColor: '#f0f7ff',
                                        borderRadius: 6,
                                        border: '1px solid #bbdefb',
                                        textAlign: direction === 'rtl' ? 'right' : 'left',
                                    }}
                                >
                                    <h6 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: '#0d47a1' }}>
                                        {t('BmkvdReconciliations')}
                                    </h6>
                                    <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px' }}>
                                        {t('BmkvdReconciliationsHelp')}
                                    </p>

                                    {!reconciliations.isAvailable ? (
                                        <p style={{ fontSize: 13, color: '#8a6d00', margin: 0 }}>
                                            {reconciliations.unavailableReason || t('BmkvdReconciliationsNone')}
                                        </p>
                                    ) : (
                                        <>
                                            <p style={{ fontSize: 13, color: '#444', margin: '0 0 8px' }}>
                                                {t('BmkvdReconciliationsInFile')}: <strong>{reconciliations.totalCount}</strong>
                                                {' | '}
                                                {t('BmkvdReconciliationsToCreate')}: <strong>{reconciliations.importableCount}</strong>
                                                {' | '}
                                                {t('BmkvdReconciliationsAccounts')}: <strong>{reconciliations.accountCount}</strong>
                                                {reconciliations.skipped?.length > 0 && (
                                                    <>
                                                        {' | '}
                                                        {t('BmkvdReconciliationsSkipped')}: <strong>{reconciliations.skipped.length}</strong>
                                                    </>
                                                )}
                                            </p>

                                            {reconciliations.bankReconciledMovementCount > 0 && (
                                                <p style={{ fontSize: 12, color: '#1565c0', margin: '0 0 8px' }}>
                                                    {t('BmkvdBankReconciledNotice', {
                                                        count: reconciliations.bankReconciledMovementCount,
                                                    })}
                                                </p>
                                            )}

                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={importReconciliations}
                                                        disabled={!reconciliations.importableCount}
                                                        onChange={(e) => setImportReconciliations(e.target.checked)}
                                                    />
                                                }
                                                label={
                                                    <span style={{ fontSize: 13 }}>
                                                        {t('BmkvdImportReconciliations', {
                                                            count: reconciliations.importableCount,
                                                        })}
                                                    </span>
                                                }
                                            />

                                            {reconciliations.importableCount > 0 && (
                                                <>
                                                    <TextField
                                                        size="small"
                                                        placeholder={t('BmkvdReconciliationsSearch')}
                                                        value={reconciliationSearch}
                                                        onChange={(e) => {
                                                            setReconciliationSearch(e.target.value);
                                                            setReconciliationsPage(0);
                                                        }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon fontSize="small" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{ width: 320, mb: 1, backgroundColor: '#fff' }}
                                                    />

                                                    <table
                                                        className="sortcode-table"
                                                        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
                                                    >
                                                        <thead>
                                                            <tr style={{ backgroundColor: '#e8e8e8' }}>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                    {t('BmkvdReconciliationNumber')}
                                                                </th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                    {t('AccountCard')}
                                                                </th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                    {t('DocumentDate')}
                                                                </th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                                    {t('TotalDebit')}
                                                                </th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                                    {t('TotalCredit')}
                                                                </th>
                                                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                    {t('BmkvdReconciliationEntries')}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {pagedReconciliations.map((item) => (
                                                                <tr key={`${item.accountCode}-${item.fileNumber}`}>
                                                                    <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                        {item.fileNumber}
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                        {[item.cardId, item.accountName].filter(Boolean).join(' - ')}
                                                                        {item.cardId !== item.accountCode && (
                                                                            <span style={{ color: '#888' }}> ({item.accountCode})</span>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                        {formatDate(item.transactionDate)}
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                                        {formatAmount(item.debitTotal)}
                                                                        <span style={{ color: '#888' }}> ({item.debitCount})</span>
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>
                                                                        {formatAmount(item.creditTotal)}
                                                                        <span style={{ color: '#888' }}> ({item.creditCount})</span>
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                        {(item.legs ?? []).map((leg) => leg.lineNumber).join(', ')}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>

                                                    <TablePagination
                                                        component="div"
                                                        count={filteredReconciliations.length}
                                                        page={reconciliationsPage}
                                                        onPageChange={(e, page) => setReconciliationsPage(page)}
                                                        rowsPerPage={reconciliationsRowsPerPage}
                                                        onRowsPerPageChange={(e) => {
                                                            setReconciliationsRowsPerPage(parseInt(e.target.value, 10));
                                                            setReconciliationsPage(0);
                                                        }}
                                                        rowsPerPageOptions={[10, 25, 50]}
                                                        labelRowsPerPage={t('RowsPerPage')}
                                                    />
                                                </>
                                            )}

                                            {reconciliations.skipped?.length > 0 && (
                                                <>
                                                    <Button
                                                        size="small"
                                                        onClick={() => setShowSkippedReconciliations(v => !v)}
                                                        sx={{ mt: 1, fontSize: 12 }}
                                                    >
                                                        {showSkippedReconciliations
                                                            ? t('Hide')
                                                            : t('BmkvdShowSkippedReconciliations', {
                                                                count: reconciliations.skipped.length,
                                                            })}
                                                    </Button>

                                                    {showSkippedReconciliations && (
                                                        <table
                                                            className="sortcode-table"
                                                            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}
                                                        >
                                                            <thead>
                                                                <tr style={{ backgroundColor: '#e8e8e8' }}>
                                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                        {t('BmkvdReconciliationNumber')}
                                                                    </th>
                                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                        {t('AccountCard')}
                                                                    </th>
                                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                        {t('Reason')}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {reconciliations.skipped.map((item) => (
                                                                    <tr key={`${item.accountCode}-${item.fileNumber}-skipped`}>
                                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                            {item.fileNumber}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                            {[item.accountCode, item.accountName].filter(Boolean).join(' - ')}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                                            {item.reason}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Missing Sortcodes Validation Table */}
                            {editableMissing.length === 0 ? (
                                <p style={{ color: 'green' }}>{t('NoMissingSortCodes') || 'אין סוגי תנועות חסרים'}</p>
                            ) : (
                                <>
                                    <h5>{t('MissingSortCodes') || 'סוגי תנועות חסרים'}</h5>
                                    <p style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                                        {t('BmkvdMissingSortCodesHelp')}
                                    </p>
                                    <p style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                                        {t('BmkvdAssignExistingSortCodeHelp')}
                                    </p>
                                    {/* Bulk Edit Controls */}
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                                        {renderPicker({
                                            placeholder: t('SelectSortCodeVatType'),
                                            options: formattedSortCodeVatType,
                                            value: bulkVatType,
                                            onChange: (val) => {
                                                setBulkVatType(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, vatType: true }));
                                            },
                                            width: 180,
                                        })}
                                        {renderPicker({
                                            placeholder: t('SelectSortCodeGroupName'),
                                            options: formattedSortCodesGroupNamesOptions,
                                            value: bulkGroup,
                                            onChange: (val) => {
                                                setBulkGroup(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, group: true }));
                                            },
                                            width: 180,
                                        })}
                                        {renderPicker({
                                            placeholder: t('participatesInProfitAndLoss'),
                                            options: participationOptions,
                                            value: bulkPar,
                                            onChange: (val) => {
                                                setBulkPar(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, par: true }));
                                            },
                                            width: 140,
                                        })}
                                        {renderPicker({
                                            placeholder: t('VAT'),
                                            options: vatPercentageOptions,
                                            value: bulkVat,
                                            onChange: (val) => {
                                                setBulkVat(val);
                                                setBulkFieldsChanged(prev => ({ ...prev, vat: true }));
                                            },
                                            width: 100,
                                        })}
                                        <BorderBoxButton
                                            variant="outlined"
                                            disabled={!bulkFieldsChanged.vatType && !bulkFieldsChanged.group && !bulkFieldsChanged.par && !bulkFieldsChanged.vat}
                                            onClick={applyBulk}
                                        >
                                            {t('ApplyToSelected')}
                                        </BorderBoxButton>
                                    </div>

                                    <table className="sortcode-table editable">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '5%' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                                        onChange={e => setSelectedRows(e.target.checked ? editableMissing.map(r => r.sortCodeId) : [])}
                                                    />
                                                </th>
                                                <th>{t('BmkvdAssignExistingSortCode')}</th>
                                                <th>{t('SortCode Id')}</th>
                                                <th>{t('SortCode Name')}</th>
                                                <th>{t('SelectSortCodeVatType')}</th>
                                                <th>{t('SelectSortCodeGroupName')}</th>
                                                <th>{t('participatesInProfitAndLoss')}</th>
                                                <th>{t('VAT')}</th>
                                                <th>{t('Quantity')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editableMissing.map((sc, i) => {
                                                const assignedTo = sortCodeAssignments[sc.sortCodeName] || null;

                                                return (
                                                <tr key={sc.sortCodeId} style={assignedTo ? { opacity: 0.6 } : undefined}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRows.includes(sc.sortCodeId)}
                                                            onChange={e => setSelectedRows(prev => e.target.checked ? [...prev, sc.sortCodeId] : prev.filter(id => id !== sc.sortCodeId))}
                                                        />
                                                    </td>
                                                    <td>
                                                        {renderPicker({
                                                            placeholder: t('BmkvdCreateNewSortCode'),
                                                            options: existingSortCodeOptions,
                                                            value: existingSortCodeOptions.find(o => o.value === assignedTo) || null,
                                                            onChange: opt => setSortCodeAssignments(prev => {
                                                                const next = { ...prev };
                                                                if (opt) next[sc.sortCodeName] = opt.value;
                                                                else delete next[sc.sortCodeName];
                                                                return next;
                                                            }),
                                                        })}
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeId}
                                                            variant="standard"
                                                            InputProps={{
                                                                readOnly: true,
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: '#F5F5F6',
                                                                    border: '1px solid transparent'
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeName || ''}
                                                            variant="standard"
                                                            onChange={e => updateMissing(i, 'sortCodeName', e.target.value)}
                                                            placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                            helperText={sc.sampleAccounts || ''}
                                                            InputProps={{
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? 'white' : '#F5F5F6',
                                                                    border: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? '1.5px solid #f44336' : '1px solid transparent',
                                                                },
                                                            }}
                                                            inputProps={{
                                                                style: {
                                                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                                                },
                                                            }}
                                                            FormHelperTextProps={{
                                                                sx: { fontSize: 10, margin: '2px 4px 0', color: '#888' },
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        {renderPicker({
                                                            placeholder: t('SelectSortCodeVatType'),
                                                            options: formattedSortCodeVatType,
                                                            value: formattedSortCodeVatType.find(opt => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null,
                                                            onChange: opt => updateMissing(i, 'sortCodeVatType', opt?.value ?? null),
                                                            error: sc.sortCodeVatType == null,
                                                        })}
                                                    </td>
                                                    <td>
                                                        {renderPicker({
                                                            placeholder: t('SelectSortCodeGroupName'),
                                                            options: formattedSortCodesGroupNamesOptions,
                                                            value: formattedSortCodesGroupNamesOptions.find(opt => opt.value === sc.sortCodeCategory) || null,
                                                            onChange: opt => updateMissing(i, 'sortCodeCategory', opt?.value ?? null),
                                                        })}
                                                    </td>
                                                    <td>
                                                        {renderPicker({
                                                            placeholder: t('participatesInProfitAndLoss'),
                                                            options: participationOptions,
                                                            value: participationOptions.find(opt => opt.value === sc.participatesInProfitAndLoss) || null,
                                                            onChange: opt => updateMissing(i, 'participatesInProfitAndLoss', opt?.value ?? null),
                                                            error: sc.participatesInProfitAndLoss == null,
                                                        })}
                                                    </td>
                                                    <td>
                                                        {renderPicker({
                                                            placeholder: t('VAT'),
                                                            options: vatPercentageOptions,
                                                            value: vatPercentageOptions.find(opt => opt.value === sc.vat) || null,
                                                            onChange: opt => updateMissing(i, 'vat', opt?.value ?? null),
                                                            error: sc.vat == null,
                                                        })}
                                                    </td>
                                                    <td style={{ textAlign: 'center', fontSize: 12 }}>{sc.movementCount || ''}</td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {/* Cards to be auto-created on commit (informational only — not a blocker). */}
                            {missingAccountCards.length > 0 && (
                                <div style={{ marginTop: 25, padding: 12, backgroundColor: '#e8f4fd', border: '1px solid #2196f3', borderRadius: 6 }}>
                                    <h5 style={{ marginTop: 0, marginBottom: 8, color: '#0d47a1' }}>
                                        {t('CardsToAutoCreate') || 'כרטיסים שייווצרו אוטומטית בעת הייבוא'} ({missingAccountCards.length})
                                    </h5>
                                    <p style={{ fontSize: 12, color: '#1565c0', marginBottom: 8 }}>
                                        {t('CardsToAutoCreateHelp')}
                                    </p>
                                    <p style={{ fontSize: 12, color: '#1565c0', marginBottom: 8 }}>
                                        {t('BmkvdJournalIncludesCardsHelp')}
                                    </p>
                                    {fileStats?.conflictingCardIdCount > 0 && (
                                        <p style={{ fontSize: 12, color: '#b71c1c', marginBottom: 8 }}>
                                            {t('BmkvdCardIdConflictHelp')}
                                        </p>
                                    )}
                                    {missingPalCodes.length > 0 && (
                                        <div style={{ marginBottom: 15, padding: 12, backgroundColor: '#fff3cd', borderRadius: 5, border: '1px solid #ffc107' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 10 }}>
                                                <div>
                                                    <h5 style={{ color: '#856404', margin: 0 }}>
                                                        {t('MissingPalCodes')} ({missingPalCodes.length})
                                                    </h5>
                                                    <p style={{ fontSize: 12, color: '#856404', margin: '5px 0 0 0' }}>
                                                        {t('BmkvdMissingPalCodesHelp')}
                                                    </p>
                                                </div>
                                                <Button
                                                    variant={palCodeBulkMode ? 'contained' : 'outlined'}
                                                    size="small"
                                                    onClick={() => {
                                                        setPalCodeBulkMode(!palCodeBulkMode);
                                                        setSelectedPalCodesForBulk([]);
                                                        setEditingPalCode(null);
                                                        if (!palCodeBulkMode) {
                                                            setPalCodeBulkForm({ palCodeType: '', palCodeCategory: null });
                                                        }
                                                    }}
                                                >
                                                    {palCodeBulkMode ? t('CancelBulk') : t('BulkCreate')}
                                                </Button>
                                            </div>

                                            {palCodeBulkMode && (
                                                <div style={{ marginBottom: 15, padding: 12, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                                                    <h6 style={{ marginBottom: 10 }}>{t('BulkCreatePalCodes')}</h6>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
                                                            {t('PalCodeType')} <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        {renderPicker({
                                                            placeholder: t('Select'),
                                                            options: palCodeTypeOptions,
                                                            value: palCodeTypeOptions.find(o => o.value === palCodeBulkForm.palCodeType) || null,
                                                            onChange: (o) => setPalCodeBulkForm({ ...palCodeBulkForm, palCodeType: o?.value || '' }),
                                                        })}
                                                    </div>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
                                                            {t('PalCodeCategory')}
                                                        </label>
                                                        {renderPicker({
                                                            placeholder: t('None'),
                                                            options: palCodeCategorySelectOptions,
                                                            value: palCodeCategorySelectOptions.find(o => o.value === palCodeBulkForm.palCodeCategory) || null,
                                                            onChange: (o) => setPalCodeBulkForm({ ...palCodeBulkForm, palCodeCategory: o?.value || null }),
                                                        })}
                                                    </div>
                                                    <p style={{ fontSize: 12, color: '#666', margin: '10px 0 5px' }}>
                                                        {t('SelectedPalCodes')}: {selectedPalCodesForBulk.length} / {missingPalCodes.length}
                                                    </p>
                                                    <Box display="flex" gap={1}>
                                                        <BorderBoxButton
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => setSelectedPalCodesForBulk(
                                                                selectedPalCodesForBulk.length === missingPalCodes.length ? [] : [...missingPalCodes],
                                                            )}
                                                        >
                                                            {selectedPalCodesForBulk.length === missingPalCodes.length
                                                                ? t('DeselectAll')
                                                                : t('SelectAll')}
                                                        </BorderBoxButton>
                                                        <BorderBoxButton
                                                            variant="contained"
                                                            size="small"
                                                            onClick={handleBulkCreatePalCodes}
                                                            disabled={processing || !palCodeBulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                                        >
                                                            {t('CreateSelected')}
                                                        </BorderBoxButton>
                                                    </Box>
                                                </div>
                                            )}

                                            {missingPalCodes.map((palCodeName, idx) => (
                                                <div key={`${palCodeName}-${idx}`} style={{ marginBottom: 10, padding: 10, backgroundColor: '#fff', borderRadius: 3 }}>
                                                    {editingPalCode === palCodeName ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                            <TextField
                                                                label={t('PalCodeId')}
                                                                value={palCodeForm.palCodeId}
                                                                size="small"
                                                                fullWidth
                                                                disabled
                                                            />
                                                            <div>
                                                                <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
                                                                    {t('PalCodeType')} <span style={{ color: 'red' }}>*</span>
                                                                </label>
                                                                {renderPicker({
                                                                    placeholder: t('Select'),
                                                                    options: palCodeTypeOptions,
                                                                    value: palCodeTypeOptions.find(o => o.value === palCodeForm.palCodeType) || null,
                                                                    onChange: (o) => setPalCodeForm({ ...palCodeForm, palCodeType: o?.value || '' }),
                                                                })}
                                                            </div>
                                                            <div>
                                                                <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
                                                                    {t('PalCodeCategory')}
                                                                </label>
                                                                {renderPicker({
                                                                    placeholder: t('None'),
                                                                    options: palCodeCategorySelectOptions,
                                                                    value: palCodeCategorySelectOptions.find(o => o.value === palCodeForm.palCodeCategory) || null,
                                                                    onChange: (o) => setPalCodeForm({ ...palCodeForm, palCodeCategory: o?.value || null }),
                                                                })}
                                                            </div>
                                                            <Box display="flex" gap={1}>
                                                                <BorderBoxButton
                                                                    variant="contained"
                                                                    size="small"
                                                                    onClick={() => handleCreatePalCode(palCodeName)}
                                                                    disabled={processing || !palCodeForm.palCodeType}
                                                                >
                                                                    {t('create')}
                                                                </BorderBoxButton>
                                                                <BorderBoxButton
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setEditingPalCode(null);
                                                                        setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
                                                                    }}
                                                                >
                                                                    {t('Cancel')}
                                                                </BorderBoxButton>
                                                            </Box>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                {palCodeBulkMode && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedPalCodesForBulk.includes(palCodeName)}
                                                                        onChange={(e) => setSelectedPalCodesForBulk(prev => (e.target.checked
                                                                            ? [...prev, palCodeName]
                                                                            : prev.filter(pc => pc !== palCodeName)))}
                                                                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                                    />
                                                                )}
                                                                <span style={{ fontWeight: 'bold' }}>{palCodeName}</span>
                                                                <span style={{ fontSize: 11, color: '#666' }}>
                                                                    ({missingAccountCards.filter(c => c.trialBalanceName === palCodeName).length})
                                                                </span>
                                                            </div>
                                                            {!palCodeBulkMode && (
                                                                <BorderBoxButton
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setEditingPalCode(palCodeName);
                                                                        setPalCodeForm({
                                                                            palCodeId: palCodeName,
                                                                            palCodeType: '',
                                                                            palCodeCategory: null,
                                                                        });
                                                                    }}
                                                                >
                                                                    {t('CreatePalCode')}
                                                                </BorderBoxButton>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#bbdefb' }}>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9' }}>{t('CardId')}</th>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9' }}>{t('BmkvdFileAccountCode')}</th>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('CardName')}</th>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('PalCode')}</th>
                                                <th style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: 'center' }}>{t('BmkvdMovements')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {missingAccountCards.map((card, idx) => (
                                                <tr
                                                    key={`${card.cardId ?? idx}`}
                                                    style={{ backgroundColor: card.hasCardIdConflict ? '#fff3e0' : 'inherit' }}
                                                    title={card.hasCardIdConflict
                                                        ? `${t('BmkvdCardIdTakenBy')}: ${card.conflictingCardName ?? ''}`
                                                        : ''}
                                                >
                                                    <td style={{ padding: '6px 8px', border: '1px solid #90caf9' }}>
                                                        {card.cardId || ''}
                                                        {card.hasCardIdConflict && (
                                                            <span style={{ color: '#e65100', fontSize: 11, marginInlineStart: 4 }}>
                                                                ({t('BmkvdNewCardId')})
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', border: '1px solid #90caf9' }}>
                                                        {card.fileAccountCode || ''}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {card.cardName || card.cardId || ''}
                                                        {card.hasCardIdConflict && card.conflictingCardName && (
                                                            <div style={{ fontSize: 11, color: '#e65100' }}>
                                                                {t('BmkvdCardIdTakenBy')}: {card.conflictingCardName}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {card.palCode || (
                                                            <span style={{ color: '#b71c1c', fontSize: 11 }}>
                                                                {card.trialBalanceName
                                                                    ? `${t('BmkvdPalCodeNotFound')}: ${card.trialBalanceName}`
                                                                    : t('BmkvdPalCodeNotFound')}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', border: '1px solid #90caf9', textAlign: 'center' }}>
                                                        {card.movementCount ?? ''}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Journal Entries Preview — MUI table with search & pagination */}
                            {entries.length > 0 && (
                                <Box mt={3}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {t('JournalEntries')} ({filteredEntries.length}
                                            {hasActiveFilters ? ` / ${entries.length}` : ''})
                                        </Typography>
                                        {hasActiveFilters && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    resetFilters();
                                                    setEntriesSearch('');
                                                    setEntriesPage(0);
                                                }}
                                            >
                                                {t('ClearFilters') || 'נקה מסננים'}
                                            </Button>
                                        )}
                                    </Box>

                                    <Box display="flex" flexWrap="wrap" gap={1.5} mb={1.5} alignItems="center">
                                        <TextField
                                            size="small"
                                            placeholder={t('Search')}
                                            value={entriesSearch}
                                            onChange={(ev) => {
                                                setEntriesSearch(ev.target.value);
                                                setEntriesPage(0);
                                            }}
                                            sx={{ width: 240 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {renderFilterSelect({
                                            label: t('BmkvdTransactionType'),
                                            options: transactionTypeOptions,
                                            value: filterTransactionTypes,
                                            onChange: setFilterTransactionTypes,
                                            width: 200,
                                        })}
                                        {renderFilterSelect({
                                            label: t('SortCode Id'),
                                            options: sortCodeFilterOptions,
                                            value: filterSortCodeIds,
                                            onChange: setFilterSortCodeIds,
                                            width: 220,
                                        })}
                                        {renderFilterSelect({
                                            label: t('DebitAccount'),
                                            options: debitAccountOptions,
                                            value: filterDebitAccounts,
                                            onChange: setFilterDebitAccounts,
                                            width: 180,
                                        })}
                                        {renderFilterSelect({
                                            label: t('CreditAccount'),
                                            options: creditAccountOptions,
                                            value: filterCreditAccounts,
                                            onChange: setFilterCreditAccounts,
                                            width: 180,
                                        })}
                                        {renderFilterSelect({
                                            label: t('BmkvdPairing'),
                                            options: pairingModeOptions,
                                            value: filterPairingModes,
                                            onChange: setFilterPairingModes,
                                            width: 200,
                                        })}
                                        {renderFilterSelect({
                                            label: t('Status'),
                                            options: statusFilterOptions,
                                            value: filterStatus,
                                            onChange: setFilterStatus,
                                            isMulti: false,
                                            width: 150,
                                        })}
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {renderEntriesSortHeader('lineNumber', '#')}
                                                    {renderEntriesSortHeader('documentDate', t('DocumentDate'))}
                                                    {renderEntriesSortHeader('reference', t('Reference'))}
                                                    {renderEntriesSortHeader('transactionNumber', t('BmkvdTransactionNumber'))}
                                                    {renderEntriesSortHeader('batchNumber', t('BmkvdBatchNumber'))}
                                                    {renderEntriesSortHeader('debitAccount', t('DebitAccount'))}
                                                    {renderEntriesSortHeader('debitAccountName', t('DebitName'))}
                                                    {renderEntriesSortHeader('creditAccount', t('CreditAccount'))}
                                                    {renderEntriesSortHeader('creditAccountName', t('CreditName'))}
                                                    {renderEntriesSortHeader('amount', t('Amount'), 'right')}
                                                    {renderEntriesSortHeader('description', t('Description'))}
                                                    {renderEntriesSortHeader('transactionType', t('BmkvdTransactionType'))}
                                                    {renderEntriesSortHeader('sortCodeId', t('SortCode Id'))}
                                                    {renderEntriesSortHeader('pairingMode', t('BmkvdPairing'))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paginatedEntries.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={14} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                            {t('NoResults') || t('Search')}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    paginatedEntries.map((e) => (
                                                        <TableRow
                                                            key={`${e.lineNumber}-${e.transactionNumber ?? ''}-${e.amount}`}
                                                            hover
                                                            sx={{
                                                                // A duplicate is only marked out when it is being left behind.
                                                                backgroundColor: e.isDuplicate && skipDuplicates
                                                                    ? '#fff8e1'
                                                                    : !e.isValid
                                                                        ? '#fdecea'
                                                                        : 'inherit',
                                                            }}
                                                            title={e.errorMessage || ''}
                                                        >
                                                            <TableCell sx={{ fontSize: 12 }}>{e.lineNumber}</TableCell>
                                                            <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(e.documentDate)}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{e.reference || ''}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{e.transactionNumber || ''}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{e.batchNumber || ''}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>
                                                                {e.debitAccount ? e.debitAccount : (
                                                                    <Typography component="span" variant="body2" color="error">
                                                                        {t('BmkvdMissingSide')}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: 12, maxWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {e.debitAccountName || ''}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>
                                                                {e.creditAccount ? e.creditAccount : (
                                                                    <Typography component="span" variant="body2" color="error">
                                                                        {t('BmkvdMissingSide')}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: 12, maxWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {e.creditAccountName || ''}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(e.amount)}</TableCell>
                                                            <TableCell sx={{ fontSize: 12, maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {e.description || ''}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{e.transactionType || ''}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>
                                                                {e.sortCodeId ? e.sortCodeId : (
                                                                    <Typography component="span" variant="body2" color="primary">
                                                                        {t('Auto')}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                                {formatPairingMode(e.pairingMode, t)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={sortedEntries.length}
                                            page={entriesPage}
                                            onPageChange={(_, p) => setEntriesPage(p)}
                                            rowsPerPage={entriesRowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setEntriesRowsPerPage(parseInt(e.target.value, 10));
                                                setEntriesPage(0);
                                            }}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            labelRowsPerPage={t('RowsPerPage')}
                                            SelectProps={{
                                                MenuProps: {
                                                    // Modal overlay sits at z-index 2000-2002; lift the
                                                    // rows-per-page dropdown above it so it isn't hidden.
                                                    sx: { zIndex: 2100 },
                                                },
                                            }}
                                        />
                                    </TableContainer>
                                </Box>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BmkvdJournalImportModal;
